import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Close } from "@material-ui/icons";
import styled from "styled-components";
import { ExerciseState } from "routes/createContent/api/usePutExerciseState";
import { CheckboxSelector } from "./CheckboxSelector";

interface FilterBoxProps {
  joints: string[];
  selectedJoints: string[];
  setSelectedJoints: (joints: string[]) => void;
  positions: string[];
  selectedPositions: string[];
  setSelectedPositions: (positions: string[]) => void;
  movements: string[];
  selectedMovements: string[];
  setSelectedMovements: (movements: string[]) => void;
  equipments: string[];
  selectedEquipments: string[];
  setSelectedEquipments: (equipments: string[]) => void;
  selectedState: ExerciseState | "";
  setSelectedState: (state: ExerciseState | "") => void;
  t: (key: string) => string;
}

const StyledAccordion = styled(Accordion)`
  background-color: #f5f5f5;
  border: 1px solid #e0e4e8;
  border-radius: 8px !important;
  margin-bottom: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:before {
    display: none;
  }

  .MuiAccordionSummary-root {
    background-color: #ffffff;
    border-bottom: 1px solid #e0e4e8;
    border-radius: 8px 8px 0 0;
    min-height: 56px;
    padding: 16px;

    &.Mui-expanded {
      min-height: 56px;
      border-radius: 8px 8px 0 0;
    }
  }

  .MuiAccordionDetails-root {
    padding: 24px;
    background-color: #f5f5f5;
  }

  &.Mui-expanded {
    margin: 0 0 16px 0;
  }
`;

export const FilterBox: React.FC<FilterBoxProps> = ({
  joints,
  selectedJoints,
  setSelectedJoints,
  positions,
  selectedPositions,
  setSelectedPositions,
  movements,
  selectedMovements,
  setSelectedMovements,
  equipments,
  selectedEquipments,
  setSelectedEquipments,
  selectedState,
  setSelectedState,
  t,
}) => {
  const [expandedAccordions, setExpandedAccordions] = useState<{ [key: string]: boolean }>(() => {
    const saved = localStorage.getItem("exerciseFiltersExpanded");
    return saved ? JSON.parse(saved) : { filters: true };
  });

  const handleAccordionChange = (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    const newState = { ...expandedAccordions, [panel]: isExpanded };
    setExpandedAccordions(newState);
    localStorage.setItem("exerciseFiltersExpanded", JSON.stringify(newState));
  };

  const handleClearFilters = () => {
    setSelectedJoints([]);
    setSelectedPositions([]);
    setSelectedMovements([]);
    setSelectedEquipments([]);
    setSelectedState("");
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedJoints.length > 0) count += selectedJoints.length;
    if (selectedPositions.length > 0) count += selectedPositions.length;
    if (selectedMovements.length > 0) count += selectedMovements.length;
    if (selectedEquipments.length > 0) count += selectedEquipments.length;
    if (selectedState) count += 1;
    return count;
  };

  const renderFilterSummary = () => {
    const summary = [];

    selectedJoints.forEach(joint => {
      summary.push(<Chip key={`joint-${joint}`} label={joint.replace("_", " ")} size="small" />);
    });

    selectedPositions.forEach(position => {
      summary.push(<Chip key={`position-${position}`} label={position.replace("_", " ")} size="small" />);
    });

    selectedMovements.forEach(movement => {
      summary.push(<Chip key={`movement-${movement}`} label={movement.replace("_", " ")} size="small" />);
    });

    selectedEquipments.forEach(equipment => {
      summary.push(<Chip key={`equipment-${equipment}`} label={equipment.replace("_", " ")} size="small" />);
    });

    if (selectedState) {
      summary.push(
        <Chip key="state" label={selectedState.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())} size="small" />
      );
    }

    return summary;
  };

  return (
    <StyledAccordion expanded={expandedAccordions.filters} onChange={handleAccordionChange("filters")}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center" flexWrap="wrap" width="100%" style={{ gap: "8px" }}>
          <Typography variant="h6">{t("exercises.filters")}</Typography>
          {getActiveFiltersCount() > 0 && (
            <>
              <Box display="flex" ml={2} flexWrap="wrap" style={{ gap: "4px" }}>
                {renderFilterSummary()}
              </Box>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  handleClearFilters();
                }}
                style={{ marginLeft: "8px" }}
              >
                <Close fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box style={{ width: "100%" }}>
          <Box mb={3}>
            <CheckboxSelector
              label={t("exercises.filter_by_joints")}
              items={joints}
              selectedItems={selectedJoints}
              setSelectedItems={setSelectedJoints}
            />
          </Box>

          <Box mb={3}>
            <CheckboxSelector
              label={t("exercises.filter_by_position")}
              items={positions}
              selectedItems={selectedPositions}
              setSelectedItems={setSelectedPositions}
            />
          </Box>

          <Box mb={3}>
            <CheckboxSelector
              label={t("exercises.filter_by_movement")}
              items={movements}
              selectedItems={selectedMovements}
              setSelectedItems={setSelectedMovements}
            />
          </Box>

          <Box mb={3}>
            <CheckboxSelector
              label={t("exercises.filter_by_equipment")}
              items={equipments}
              selectedItems={selectedEquipments}
              setSelectedItems={setSelectedEquipments}
            />
          </Box>

          <Box mb={3}>
            <FormControl component="fieldset">
              <FormLabel component="legend">{t("exercises.filter_by_state")}</FormLabel>
              <RadioGroup
                row
                value={selectedState}
                onChange={e => setSelectedState(e.target.value as ExerciseState | "")}
              >
                <FormControlLabel value="" control={<Radio color="primary" />} label={t("exercises.state.all")} />
                {(["draft", "in_review", "approved", "rejected"] as ExerciseState[]).map(state => (
                  <FormControlLabel
                    key={state}
                    value={state}
                    control={<Radio color="primary" />}
                    label={state.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button variant="outlined" onClick={handleClearFilters}>
              {t("exercises.clear_filters")}
            </Button>
          </Box>
        </Box>
      </AccordionDetails>
    </StyledAccordion>
  );
};
